import { Component, ElementRef, QueryList, ViewChildren } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  PRODUCT_KEY_CONSTANT,
  DESTINATION_KEY_CONSTANT,
  ACCOUNT_LOGIN_ROUTE,
  LOGIN_OBJECT_KEY_CONSTANT,
  DEFAULT_ROUTE_CONSTANT, EMAIL_KEY_CONSTANT,
} from '../constants/routes.constant';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FirebaseAuthService } from '../services/firebase-auth.service';
import { Subject, mergeMap } from 'rxjs';
import { MatStepper } from '@angular/material/stepper';
import { PROGRESS_BAR_SUBJECT } from '../app.component';
import {UserProfileService} from "../services/user-profile.service";

@Component({
    selector: 'app-change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.scss'],
    standalone: false
})
export class ChangePasswordComponent {
  public formGroup!: FormGroup;
  public hide: boolean = true;
  public hideConfirm: boolean = true;
  public form!: FormArray;
  public MAX_CONTROLS_LENGTH: number = 6;
  @ViewChildren('otpBoxes', { read: ElementRef<HTMLInputElement> })
  public otpBoxes!: QueryList<ElementRef<HTMLInputElement>>;
  private code!: string;
  private encodedEmailKey: string = EMAIL_KEY_CONSTANT;
  private productNameKey: string = PRODUCT_KEY_CONSTANT;
  private destinationKey: string = DESTINATION_KEY_CONSTANT;
  private loginObjectKey: string = LOGIN_OBJECT_KEY_CONSTANT;
  private productName!: string;
  private destination!: string;
  private loginObject!: string;
  public queryParams: { [key: string]: string } = {
    [this.productNameKey]: this.productName,
    [this.destinationKey]: this.destination,
    [this.loginObjectKey]: this.loginObject,
  };
  public inProgress: boolean = false;
  public providedEmail!: string;
  public loginRoute: string = ACCOUNT_LOGIN_ROUTE;
  public subject: Subject<boolean> = PROGRESS_BAR_SUBJECT;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly snackBar: MatSnackBar,
    private readonly userProfileService: UserProfileService,
  ) {}

  ngOnInit() {
    this.initializeForm();

    this.providedEmail = this.activatedRoute.snapshot.paramMap.get(
      this.encodedEmailKey
    )!;
    this.productName = this.activatedRoute.snapshot.queryParamMap.get(
      this.productNameKey
    )!;
    this.destination = this.activatedRoute.snapshot.queryParamMap.get(
      this.destinationKey
    )!;

    this.queryParams = {
      [this.productNameKey]: this.productName,
      [this.destinationKey]: this.destination,
      [this.loginObjectKey]: this.loginObject,
    };

    if (!this.productName || !this.destination) {
      this.router.navigate(DEFAULT_ROUTE_CONSTANT, {
        queryParams: this.queryParams,
      });
    }

    this.initializeListeners();
  }

  public get getLoginRoute() {
    return ['/', ...this.loginRoute.split('/')];
  }

  public resetPassword() {
    this.subject.next(true);

    const data = this.formGroup.getRawValue();
    const email = this.providedEmail;

    this.userProfileService
      .completePasswordResetEmail(data.code, atob(email), data.password)
      .subscribe({
        next: () => {
          this.subject.next(false);
          this.inProgress = false;
          this.snackBar.open('Completed!', 'close', {
            verticalPosition: 'top',
            horizontalPosition: 'end',
            panelClass: [
              'bg-primary',
              'text-white',
              'rounded-3e',
              'modified-snackbar',
            ],
          });

          this.form.reset();
          this.formGroup.reset();
          this.router.navigate(['/', ...this.loginRoute.split('/')], {
            queryParams: this.queryParams,
          });
        },
        error: () => {
          this.subject.next(false);

          this.inProgress = false;
          this.snackBar.open('An error occurred!', 'close', {
            verticalPosition: 'top',
            horizontalPosition: 'end',
            panelClass: [
              'bg-danger',
              'text-white',
              'rounded-3e',
              'modified-snackbar',
            ],
          });
        },
      });
  }

  public resendPasswordReset(stepper: MatStepper) {
    this.inProgress = true;

    this.userProfileService
      .resendPasswordResetEmail(this.providedEmail, this.productName, this.destination)
      .subscribe({
        next: () => {
          this.inProgress = false;
          this.snackBar.open('Sent!', 'close', {
            verticalPosition: 'top',
            horizontalPosition: 'end',
            panelClass: [
              'bg-primary',
              'text-white',
              'rounded-3e',
              'modified-snackbar',
            ],
          });
          this.form.reset();
          stepper.previous();
        },
        error: () => {
          this.inProgress = false;
          this.snackBar.open('An error occurred!', 'close', {
            verticalPosition: 'top',
            horizontalPosition: 'end',
            panelClass: [
              'bg-danger',
              'text-white',
              'rounded-3e',
              'modified-snackbar',
            ],
          });
        },
      });
  }

  public resetVerificationForm(stepper: MatStepper) {
    this.code = this.form.getRawValue().join('');

    this.formGroup.controls['code'].setValue(this.code);
    this.form.reset();

    if (stepper) {
      stepper.next();
    }
  }

  public getControlAt<T = FormControl>(index: number) {
    return this.form.at(index) as T;
  }

  public onKeyUp(index: number, e: KeyboardEvent) {
    if (index >= 0 && e.code.toLowerCase() === 'backspace') {
      this.otpBoxes.get(index - 1)?.nativeElement.focus();
    } else {
      this.otpBoxes.get(index + 1)?.nativeElement.focus();
    }
  }

  public initializeListeners() {
    for (let i = 0; i < this.MAX_CONTROLS_LENGTH; i++) {
      const control = this.form.at(i);

      control.valueChanges.subscribe({
        next: () => {
          const value: string = control.value;

          if (!value || value.length < this.MAX_CONTROLS_LENGTH) return;

          this.form.setValue(
            value.split('').slice(0, this.MAX_CONTROLS_LENGTH),
            { emitEvent: false }
          );
        },
      });
    }
  }

  public initializeForm() {
    this.formGroup = this.formBuilder.nonNullable.group(
      {
        code: new FormControl(this.code, [Validators.required]),
        password: new FormControl(null, [
          Validators.required,
          Validators.minLength(8),
        ]),
        confirmPassword: new FormControl(null, [
          Validators.required,
          Validators.minLength(8),
        ]),
      },
      { validators: this.passwordMatchValidator }
    );

    this.form = this.formBuilder.array(
      Array.from({ length: this.MAX_CONTROLS_LENGTH }).map(
        () =>
          new FormControl(null, [
            Validators.required,
            Validators.minLength(1),
            Validators.maxLength(1),
          ])
      )
    );
  }

  public passwordMatchValidator(control: AbstractControl) {
    return control.get('password')!.value ===
      control.get('confirmPassword')!.value
      ? null
      : { passwordMisMatch: 'Passwords do not match' };
  }
}
